import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { configsService, SystemConfig, ConfigCategory, ConfigDataType } from '../../services/api/configs';
import './SystemSettings.css';

const SystemSettings: React.FC = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [configs, setConfigs] = useState<Record<string, SystemConfig>>({});
  const [configsByCategory, setConfigsByCategory] = useState<Record<ConfigCategory, SystemConfig[]>>({
    [ConfigCategory.LEAVE_POLICY]: [],
    [ConfigCategory.LATE_EARLY_POLICY]: [],
    [ConfigCategory.APPROVAL_POLICY]: [],
    [ConfigCategory.NOTIFICATION]: [],
    [ConfigCategory.WORK_SCHEDULE]: [],
    [ConfigCategory.SYSTEM]: [],
  });
  const [editedValues, setEditedValues] = useState<Record<string, any>>({});
  const [message, setMessage] = useState<{ type: 'success' | 'error' | 'info'; text: string } | null>(null);

  useEffect(() => {
    loadConfigs();
  }, []);

  const loadConfigs = async () => {
    try {
      setLoading(true);
      const response = await configsService.getAll({ pageSize: 1000 });
      
      const configsMap: Record<string, SystemConfig> = {};
      const byCategory: Record<ConfigCategory, SystemConfig[]> = {
        [ConfigCategory.LEAVE_POLICY]: [],
        [ConfigCategory.LATE_EARLY_POLICY]: [],
        [ConfigCategory.APPROVAL_POLICY]: [],
        [ConfigCategory.NOTIFICATION]: [],
        [ConfigCategory.WORK_SCHEDULE]: [],
        [ConfigCategory.SYSTEM]: [],
      };

      response.items.forEach((config) => {
        configsMap[config.configKey] = config;
        if (config.category) {
          byCategory[config.category].push(config);
        }
      });

      setConfigs(configsMap);
      setConfigsByCategory(byCategory);
    } catch (error) {
      console.error('Failed to load configs:', error);
      setMessage({ type: 'error', text: 'Failed to load configurations' });
    } finally {
      setLoading(false);
    }
  };

  const parseValue = (value: string, dataType: ConfigDataType): any => {
    switch (dataType) {
      case ConfigDataType.NUMBER:
        return parseFloat(value);
      case ConfigDataType.BOOLEAN:
        return value === 'true' || value === '1';
      case ConfigDataType.JSON:
        try {
          return JSON.parse(value);
        } catch {
          return value;
        }
      default:
        return value;
    }
  };

  const handleValueChange = (key: string, value: any, dataType: ConfigDataType) => {
    setEditedValues((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleSave = async () => {
    if (Object.keys(editedValues).length === 0) {
      setMessage({ type: 'info', text: 'No changes to save' });
      return;
    }

    try {
      setSaving(true);
      setMessage(null);

      const savePromises = Object.entries(editedValues).map(([key, value]) => {
        const config = configs[key];
        if (!config || !config.isEditable) return Promise.resolve();

        return configsService.updateValue(key, value);
      });

      await Promise.all(savePromises);

      setMessage({ type: 'success', text: 'Settings saved successfully' });
      setEditedValues({});
      await loadConfigs();
    } catch (error) {
      console.error('Failed to save configs:', error);
      setMessage({ type: 'error', text: 'Failed to save settings' });
    } finally {
      setSaving(false);
    }
  };

  const renderConfigInput = (config: SystemConfig) => {
    const currentValue = editedValues[config.configKey] !== undefined
      ? editedValues[config.configKey]
      : parseValue(config.configValue, config.dataType);

    if (!config.isEditable) {
      return (
        <div className="config-value-readonly">
          {String(currentValue)}
        </div>
      );
    }

    switch (config.dataType) {
      case ConfigDataType.BOOLEAN:
        return (
          <label className="config-toggle">
            <input
              type="checkbox"
              checked={currentValue}
              onChange={(e) => handleValueChange(config.configKey, e.target.checked, config.dataType)}
            />
            <span className="toggle-slider"></span>
          </label>
        );

      case ConfigDataType.NUMBER:
        return (
          <input
            type="number"
            className="config-input"
            value={currentValue}
            onChange={(e) => handleValueChange(config.configKey, parseFloat(e.target.value) || 0, config.dataType)}
          />
        );

      case ConfigDataType.JSON:
        return (
          <textarea
            className="config-textarea"
            value={JSON.stringify(currentValue, null, 2)}
            onChange={(e) => {
              try {
                const parsed = JSON.parse(e.target.value);
                handleValueChange(config.configKey, parsed, config.dataType);
              } catch {
                // Invalid JSON, keep as string
                handleValueChange(config.configKey, e.target.value, config.dataType);
              }
            }}
            rows={4}
          />
        );

      default:
        return (
          <input
            type="text"
            className="config-input"
            value={currentValue}
            onChange={(e) => handleValueChange(config.configKey, e.target.value, config.dataType)}
          />
        );
    }
  };

  const categoryLabels: Record<ConfigCategory, string> = {
    [ConfigCategory.LEAVE_POLICY]: 'Leave Policies',
    [ConfigCategory.LATE_EARLY_POLICY]: 'Late/Early Policies',
    [ConfigCategory.APPROVAL_POLICY]: 'Approval Policies',
    [ConfigCategory.NOTIFICATION]: 'Notifications',
    [ConfigCategory.WORK_SCHEDULE]: 'Work Schedule',
    [ConfigCategory.SYSTEM]: 'System',
  };

  if (loading) {
    return (
      <div className="system-settings">
        <div className="loading">Loading settings...</div>
      </div>
    );
  }

  return (
    <div className="system-settings">
      <div className="settings-header">
        <h1>System Settings</h1>
        <p>Configure system-wide settings and policies</p>
      </div>

      {message && (
        <div className={`message message-${message.type}`}>
          {message.text}
        </div>
      )}

      <div className="settings-content">
        {Object.entries(configsByCategory).map(([category, categoryConfigs]) => {
          if (categoryConfigs.length === 0) return null;

          return (
            <div key={category} className="settings-section">
              <h2 className="section-title">{categoryLabels[category as ConfigCategory]}</h2>
              <div className="configs-list">
                {categoryConfigs.map((config) => (
                  <div key={config.id} className="config-item">
                    <div className="config-label">
                      <label>{config.configKey}</label>
                      {config.description && (
                        <span className="config-description">{config.description}</span>
                      )}
                    </div>
                    <div className="config-control">
                      {renderConfigInput(config)}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      <div className="settings-footer">
        <button
          className="btn-save"
          onClick={handleSave}
          disabled={saving || Object.keys(editedValues).length === 0}
        >
          {saving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>
    </div>
  );
};

export default SystemSettings;
